import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import CreateIcon from "@mui/icons-material/Create";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { motion } from "framer-motion";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const HERO_FONT_SIZE = "clamp(2.9rem, 11vw, 6.8rem)";
const HERO_LETTER_SPACING = "clamp(0px, 0.9vw, 12px)";
const BUBBLE_SIZE = "clamp(36px, 6.4vw, 80px)";
const BUBBLE_ICON_SIZE = "clamp(18px, 3.2vw, 40px)";

const Landing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1085));

  const letterAnimation = {
    whileHover: { scale: 1.2 },
    transition: { type: "spring", stiffness: 300, damping: 15 },
  };

  const wordAnimations = {
    creative: {
      initial: { opacity: 0, scale: 0.5 },
      animate: { opacity: 1, scale: 1 },
      transition: { type: "spring", stiffness: 170, damping: 12 },
    },
    s: {
      initial: { opacity: 0, y: 100 },
      animate: { opacity: 1, y: 0 },
      transition: { type: "spring", stiffness: 150, damping: 14 },
    },
    software: {
      initial: { opacity: 0, x: -100 },
      animate: { opacity: 1, x: 0 },
      transition: { type: "spring", stiffness: 130, damping: 16 },
    },
    devel: {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      transition: { type: "spring", stiffness: 110, damping: 18 },
    },
    per: {
      initial: { opacity: 0, x: 100 },
      animate: { opacity: 1, x: 0 },
      transition: { type: "spring", stiffness: 90, damping: 20 },
    },
  };

  const Word = ({
    text,
    animation,
    reduceSpace,
  }: {
    text: string;
    animation: { initial: object; animate: object; transition: object };
    reduceSpace?: boolean;
  }) => (
    <motion.div
      initial={animation.initial}
      animate={animation.animate}
      transition={animation.transition}
      style={{ display: "inline-flex" }}
    >
      <Typography
        sx={{
          fontSize: HERO_FONT_SIZE,
          fontFamily: "inter",
          letterSpacing: reduceSpace ? "0px" : HERO_LETTER_SPACING,
          lineHeight: 1.15,
          fontWeight: 900,
          display: "flex",
          flexWrap: "nowrap",
          whiteSpace: "nowrap",
        }}
      >
        {text.split("").map((letter: string, index: number) => (
          <motion.span
            key={index}
            whileHover={letterAnimation.whileHover}
            transition={letterAnimation.transition}
            style={{ display: "inline-block" }}
          >
            {letter}
          </motion.span>
        ))}
      </Typography>
    </motion.div>
  );

  const bubbleAnimation = {
    initial: { x: "100vw", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: {
      type: "spring",
      stiffness: 150,
      damping: 20,
      duration: 2,
    },
  };

  const scrollToWork = () => {
    const workSection = document.getElementById("work");
    if (workSection) {
      workSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Box
      id="landing"
      sx={{
        minHeight: "var(--app-height, 100svh)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        padding: "clamp(90px, 12vh, 140px) clamp(20px, 5vw, 60px) clamp(48px, 8vh, 96px)",
        position: "relative",
        zIndex: 2,
        [theme.breakpoints.down(1085)]: {
          justifyContent: "flex-start",
          paddingTop: "clamp(160px, 25vh, 260px)",
          paddingRight: "24px",
          paddingBottom: "72px",
          paddingLeft: "24px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "row",
          width: "100%",
          maxWidth: "1500px",
          gap: "clamp(24px, 4vw, 64px)",
          [theme.breakpoints.down(1085)]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "40px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
            justifyContent: "center",
            gap: "clamp(4px, 1vh, 12px)",
            flex: "1 1 auto",
            minWidth: 0,
            width: "100%",
          }}
        >
          <Box
            sx={{
              ml: "clamp(0px, 8vw, 160px)",
              display: "flex",
              [theme.breakpoints.down(600)]: { ml: 0 },
            }}
          >
            <Word text="CREATIVE" animation={wordAnimations.creative} />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexDirection: "row",
            }}
          >
            <Word reduceSpace text="S" animation={wordAnimations.s} />
            <a
              href="mailto:louis.cappell@icloud.com"
              aria-label="Email Louis Cappell"
              style={{ textDecoration: "none", zIndex: 2 }}
            >
              <Box
                component={motion.div}
                {...wordAnimations.software}
                sx={{
                  width: BUBBLE_SIZE,
                  height: BUBBLE_SIZE,
                  borderRadius: "100%",
                  background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 clamp(4px, 1vw, 15px)",
                  flexShrink: 0,
                  transition: "filter 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    filter: "blur(1px)",
                    cursor: "pointer",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <CreateIcon
                  sx={{
                    width: BUBBLE_ICON_SIZE,
                    height: BUBBLE_ICON_SIZE,
                    color: "#030303",
                  }}
                />
              </Box>
            </a>
            <Word text="FTWARE" animation={wordAnimations.software} />
          </Box>
          <Box
            sx={{
              ml: "clamp(0px, 4vw, 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexDirection: "row",
              [theme.breakpoints.down(600)]: { ml: 0 },
            }}
          >
            <Word text="DEVE" animation={wordAnimations.devel} />
            <Word reduceSpace text="L" animation={wordAnimations.devel} />
            <a
              target="_blank"
              href="https://www.linkedin.com/in/louiscappell?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BVMMUvaBgRg2cKz07Yg5vcg%3D%3D"
              rel="noreferrer"
              aria-label="Louis Cappell on LinkedIn"
              style={{ zIndex: 2 }}
            >
              <Box
                component={motion.div}
                {...wordAnimations.devel}
                sx={{
                  width: BUBBLE_SIZE,
                  height: BUBBLE_SIZE,
                  borderRadius: "100%",
                  background: "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 clamp(4px, 1vw, 15px)",
                  flexShrink: 0,
                  transition: "filter 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    filter: "blur(1px)",
                    cursor: "pointer",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <LinkedInIcon
                  sx={{
                    width: BUBBLE_ICON_SIZE,
                    height: BUBBLE_ICON_SIZE,
                    color: "#030303",
                  }}
                />
              </Box>
            </a>
            <Word text="PER." animation={wordAnimations.per} />
          </Box>
        </Box>

        <Box
          component={motion.div}
          initial={bubbleAnimation.initial}
          animate={bubbleAnimation.animate}
          transition={bubbleAnimation.transition}
          sx={{
            flex: "0 0 auto",
            width: isMobile ? "100%" : "clamp(220px, 22vw, 320px)",
            display: "flex",
            justifyContent: isMobile ? "flex-start" : "center",
            alignItems: "flex-start",
          }}
        >
          <Typography
            sx={{
              fontSize: "clamp(17px, 1.5vw, 20px)",
              color: "#030303",
              fontFamily: "inter",
              fontWeight: 500,
              opacity: 0.45,
              maxWidth: "320px",
            }}
          >
            Passionate about building engaging and beautiful modern web and
            mobile applications.
          </Typography>
        </Box>
      </Box>

      <Box
        component={motion.div}
        animate={{ y: ["0%", "-3%", "0%"] }}
        transition={{
          duration: 2,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "loop",
          repeatDelay: 0,
        }}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "clamp(32px, 6vh, 72px)",
          [theme.breakpoints.down(1085)]: {
            marginTop: "auto",
            paddingTop: "48px",
          },
        }}
      >
        <Box
          onClick={scrollToWork}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transition: "opacity 0.2s ease",
            "&:hover": {
              cursor: "pointer",
              opacity: 0.7,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "clamp(15px, 1.3vw, 20px)",
              color: "#030303",
              fontFamily: "inter",
              fontWeight: 500,
              opacity: 0.45,
            }}
          >
            view more
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              width: "clamp(22px, 2vw, 30px)",
              height: "clamp(22px, 2vw, 30px)",
              color: "#030303",
              opacity: 0.45,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Landing;
